import { usePriceGuide } from 'components/providers/PriceGuideProvider'
import { useUser } from 'components/providers/UserProvider'
import { autopriceArticles } from 'lib/api'
import { useCallback } from 'react'
import { InventoryArticle, PriceGuidePricingOperation, PricingStrategy } from 'shared'
import { CancellationToken } from 'utils/cancellationToken'

export interface FetchBuyReferencePricesArgs {
  articles: InventoryArticle[]
  strategy: PricingStrategy
  cancellationToken: CancellationToken
  onProgress: (progress: number) => void
}

/**
 * Computes a reference buy price per article id for a given pricing strategy.
 * Dispatches to the autoprice API (articleData strategies) or to the client-side
 * price guide engine (priceGuide strategies).
 */
export const useBuyPriceReferences = (): ((
  args: FetchBuyReferencePricesArgs
) => Promise<Record<string, number>>) => {
  const { user, activeGame } = useUser()
  const { getPriceGuide } = usePriceGuide()

  return useCallback(
    async ({
      articles,
      strategy,
      cancellationToken,
      onProgress,
    }: FetchBuyReferencePricesArgs): Promise<Record<string, number>> => {
      const refByArticleId: Record<string, number> = {}

      if (strategy.base.kind === 'articleData') {
        const results = await autopriceArticles(
          [
            {
              articles: articles.map((a) => a.attributes),
              idGame: activeGame.idGame,
              pricingStrategyId: strategy._id,
            },
          ],
          onProgress,
          cancellationToken
        )
        for (const [id, r] of Object.entries(results)) {
          if (!r.failure && r.price != null) refByArticleId[id] = r.price
        }
        return refByArticleId
      }

      // priceGuide path — runs entirely client-side, simulate progress for UX
      if (!user) return refByArticleId

      const numOfSteps = Math.round(articles.length / 500) + 1
      for (let i = 0; i < numOfSteps; i++) {
        onProgress((i / numOfSteps) * 100)
        await new Promise((resolve) => setTimeout(resolve, 10))
      }
      onProgress(100)
      await new Promise((resolve) => setTimeout(resolve, 10))

      const chosenBulkSettingsId = strategy.bulkSettingsId[activeGame.idGame]
      const bulkSettings =
        user.bulkSettings.find((b) => b._id === chosenBulkSettingsId)?.priceMap || {}

      for (const article of articles) {
        if (!article.id) continue
        const priceGuide = getPriceGuide(article.card._id)
        if (!priceGuide) continue
        try {
          const result = new PriceGuidePricingOperation({
            article,
            strategy,
            bulkSettings,
            currency: user.currency,
            gameSettings: {
              firstEdEnabled: false,
              idGame: activeGame.idGame,
              playsetCount: activeGame.playsetCount || 4,
            },
          }).execute(priceGuide)
          if (result.price != null) refByArticleId[article.id] = result.price
        } catch {
          // missing price guide data for this article — skip
        }
      }

      return refByArticleId
    },
    [user, activeGame, getPriceGuide]
  )
}
